import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../../common/Modal';
import { messageFromError } from '../parts/errorMessage';
import { missingMandatory, yearError } from '../parts/inquiryFields';

// Turn a captured inquiry into a job.
//
// Prefilled AND editable, per the design spec: "the user must be able to
// correct a wrong model or a typo'd phone at the moment the car arrives."
// The reported pain is re-typing, so everything captured at intake arrives
// filled in -- but the car is now physically present, and that is exactly
// when a guessed model or a mistyped digit surfaces. An edit PATCHes the
// inquiry first, then converts; an unedited conversion issues one request.
//
// Mirrors the server's MIN_COMPLAINT_LENGTH: jobs.symptom_description is NOT
// NULL and JobCreateRequest enforces min_length=10, so the server returns 400
// below that. Saying so before the request beats an error toast afterwards.
const MIN_COMPLAINT_LENGTH = 10;

const PRIORITIES = [
  ['low', 'Low'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent'],
];

const field =
  'w-full rounded-card border border-border-default bg-surface-card px-3 py-3 text-ink';

// What the form should hold for a given inquiry -- used both by the
// mount/inquiry-change effect and by handleClose, so the two mechanisms can
// never disagree about what "reset" means.
const formFor = (inquiry) => ({
  customer_name: inquiry?.customer_name || '',
  customer_phone: inquiry?.customer_phone || '',
  year: inquiry?.year == null ? '' : String(inquiry.year),
  make: inquiry?.make || '',
  model: inquiry?.model || '',
  customer_email: inquiry?.customer_email || '',
  complaint: inquiry?.inquiry_notes || '',
});

// The carried fields the mechanic actually changed, shaped for PATCH
// /api/inquiries/{id}. The service uses `exclude_unset=True`, so sending only
// the changed keys is what keeps a one-field correction from blanking the
// rest. An email cleared to empty is sent as null (the column is nullable)
// rather than as '', so "no email" stays absent rather than becoming a blank
// string nothing can send to.
const changedFields = (form, inquiry) => {
  const patch = {};
  ['customer_name', 'customer_phone', 'make', 'model'].forEach((name) => {
    const next = form[name].trim();
    if (next !== (inquiry[name] || '')) patch[name] = next;
  });

  const nextYear = Number(form.year);
  if (nextYear !== Number(inquiry.year)) patch.year = nextYear;

  const nextEmail = form.customer_email.trim();
  const wasEmail = inquiry.customer_email || '';
  if (nextEmail !== wasEmail) patch.customer_email = nextEmail || null;

  return patch;
};

export const ConvertInquiryModal = ({
  inquiry,
  isOpen,
  onClose,
  onConfirm,
  onUpdate,
  submitting = false,
}) => {
  const [form, setForm] = useState(() => formFor(inquiry));
  const [priority, setPriority] = useState('normal');
  const [error, setError] = useState(null);
  // Bumped by handleClose so an in-flight convert that the mechanic has
  // already cancelled can recognise itself as abandoned when it settles, and
  // skip closing a reopened modal, clearing a form being typed into, or
  // painting a stale error over the next inquiry. Converting creates a job
  // (job_service.create_job writes to `jobs` only -- no invoice), so a
  // mechanic who can't tell whether an attempt landed may convert the same
  // inquiry twice and open two jobs for one car.
  const attemptRef = useRef(0);

  // Reset whenever a different inquiry is opened, so the previous one's
  // details cannot leak into this job.
  useEffect(() => {
    setForm(formFor(inquiry));
    setPriority('normal');
    setError(null);
  }, [inquiry]);

  // Reset on any close that is NOT a successful save (Cancel, backdrop click,
  // Escape, the X button), using the same formula as the effect above so the
  // two mechanisms agree rather than fight -- this one covers the case where
  // the modal is reopened for the SAME inquiry object (no prop change, so the
  // effect above would not re-run on its own). Bumping attemptRef here is
  // what lets a still-in-flight convert recognise, once it settles, that it
  // was cancelled.
  const handleClose = () => {
    attemptRef.current += 1;
    setForm(formFor(inquiry));
    setPriority('normal');
    setError(null);
    onClose();
  };

  if (!inquiry) return null;

  const set = (name) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missing = missingMandatory(form);
    if (missing.length > 0) {
      setError(`Still needed: ${missing.join(', ')}`);
      return;
    }
    const badYear = yearError(form.year);
    if (badYear) {
      setError(badYear);
      return;
    }

    const trimmed = form.complaint.trim();
    if (trimmed.length < MIN_COMPLAINT_LENGTH) {
      setError(
        `Describe the complaint in at least ${MIN_COMPLAINT_LENGTH} characters — ` +
          'it goes on the job and on the invoice.'
      );
      return;
    }
    setError(null);

    // Captured before the await -- see attemptRef comment above.
    const attempt = attemptRef.current;
    const patch = changedFields(form, inquiry);
    const edited = Object.keys(patch).length > 0;

    if (edited) {
      try {
        await onUpdate(inquiry.id, patch);
      } catch (err) {
        if (attempt !== attemptRef.current) return; // abandoned
        // The correction did not land, so converting now would open a job
        // carrying the wrong details the mechanic just fixed. Stop here.
        setError(
          messageFromError(
            err,
            'Could not save your corrections, so no job was opened. Please try again.'
          )
        );
        return;
      }
      if (attempt !== attemptRef.current) return; // abandoned mid-flow
    }

    try {
      await onConfirm(inquiry.id, {
        symptom_description: trimmed,
        priority,
      });
      if (attempt !== attemptRef.current) return; // abandoned -- already closed/reset by handleClose
      onClose();
    } catch (err) {
      if (attempt !== attemptRef.current) return; // abandoned -- do not paint a stale error over the next inquiry
      // Prefer the server's message: a 400 names the complaint-length
      // requirement, a 409 says the inquiry is already a job and names which
      // one. Both are genuinely useful here. messageFromError also covers the
      // 422 shape, whose `detail` is a LIST of objects -- rendering that
      // directly threw and took the whole app down with it -- and the network
      // error that has no `response` at all.
      //
      // When the PATCH already succeeded, say so: the inquiry HAS been
      // corrected and only the job is missing, and a message implying nothing
      // happened would send the mechanic hunting for a correction that is
      // already saved.
      const base = messageFromError(
        err,
        'Could not convert this inquiry. Please try again.'
      );
      setError(
        edited
          ? `Your corrections were saved, but the job was not opened: ${base}`
          : base
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Open a job" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Captured at intake, prefilled so nobody re-types it -- and
            editable, because the car is now here and the guessed model or
            mistyped digit shows up at exactly this moment. Changes are
            PATCHed onto the inquiry before the job is opened. */}
        <div className="space-y-3 rounded-card border border-border-default bg-surface-card p-4">
          <div>
            <label htmlFor="convert-name" className="block text-small text-ink-soft">
              Customer name
            </label>
            <input
              id="convert-name"
              className={field}
              value={form.customer_name}
              onChange={set('customer_name')}
            />
          </div>

          <div>
            <label htmlFor="convert-phone" className="block text-small text-ink-soft">
              Phone number
            </label>
            <input
              id="convert-phone"
              type="tel"
              className={field}
              value={form.customer_phone}
              onChange={set('customer_phone')}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="convert-year" className="block text-small text-ink-soft">
                Year
              </label>
              <input
                id="convert-year"
                type="number"
                inputMode="numeric"
                className={field}
                value={form.year}
                onChange={set('year')}
              />
            </div>
            <div>
              <label htmlFor="convert-make" className="block text-small text-ink-soft">
                Make
              </label>
              <input
                id="convert-make"
                className={field}
                value={form.make}
                onChange={set('make')}
              />
            </div>
            <div>
              <label htmlFor="convert-model" className="block text-small text-ink-soft">
                Model
              </label>
              <input
                id="convert-model"
                className={field}
                value={form.model}
                onChange={set('model')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="convert-email" className="block text-small text-ink-soft">
              Email <span className="text-ink-muted">(optional)</span>
            </label>
            <input
              id="convert-email"
              type="email"
              className={field}
              value={form.customer_email}
              onChange={set('customer_email')}
            />
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-card border border-border-default bg-surface-card px-3 py-2 text-small text-ink"
          >
            {error}
          </div>
        )}

        <div>
          <label htmlFor="convert-complaint" className="block text-small text-ink-soft">
            Customer complaint
          </label>
          <textarea
            id="convert-complaint"
            rows={3}
            className={field}
            value={form.complaint}
            onChange={set('complaint')}
          />
        </div>

        <div>
          <label htmlFor="convert-priority" className="block text-small text-ink-soft">
            Priority
          </label>
          <select
            id="convert-priority"
            className={field}
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
          >
            {PRIORITIES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="min-h-touch rounded-card px-4 py-3 text-ink-soft"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="min-h-touch rounded-card bg-accent px-5 py-3 text-white hover:bg-accent-hover disabled:opacity-60"
          >
            {submitting ? 'Opening…' : 'Open job'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ConvertInquiryModal;
